import type { ComponentType } from 'react';
import creditCardType from 'credit-card-type';
import { CreditCard } from 'lucide-react';
import {
  SiAmericanexpress,
  SiDinersclub,
  SiMastercard,
  SiVisa,
} from 'react-icons/si';
import { PaymentMethod } from '../Types';

type BrandIcon = ComponentType<{ className?: string }>;

interface BrandStyle {
  label: string;
  Icon: BrandIcon;
  /** Cor do logo (ícones usam currentColor) */
  iconColor: string;
  className: string;
}

export function getMaxParcelas(method: PaymentMethod): number {
  const cfg = method.configuracaoEspecifica;
  if (!cfg || typeof cfg !== 'object') return 1;
  const raw =
    cfg.maxParcelas ??
    cfg.qtdMaxParcelas ??
    cfg.quantidadeMaximaParcelas ??
    cfg.qtd_max_parcelas;
  const parsed = parseInt(String(raw), 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

export function luhnCheck(cardNumber: string): boolean {
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 13) return false;
  let sum = 0;
  let alternate = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits.charAt(i), 10);
    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
}

export function detectCardBrand(cardNumber: string) {
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 2) return null;
  const types = creditCardType(digits);
  return types[0] ?? null;
}

export function validateCardExpiry(expiry: string): boolean {
  const match = expiry.match(/^(\d{2})\/(\d{2})$/);
  if (!match) return false;
  const month = parseInt(match[1], 10);
  const year = 2000 + parseInt(match[2], 10);
  if (month < 1 || month > 12) return false;
  const now = new Date();
  const expiryEnd = new Date(year, month, 0, 23, 59, 59);
  const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  return expiryEnd >= startOfCurrentMonth;
}

const BRAND_STYLES: Record<string, BrandStyle> = {
  visa: {
    label: 'Visa',
    Icon: SiVisa,
    iconColor: 'text-[#1A1F71]',
    className: 'bg-blue-700 text-white',
  },
  mastercard: {
    label: 'Mastercard',
    Icon: SiMastercard,
    iconColor: 'text-[#EB001B]',
    className: 'bg-orange-600 text-white',
  },
  'american-express': {
    label: 'American Express',
    Icon: SiAmericanexpress,
    iconColor: 'text-[#006FCF]',
    className: 'bg-indigo-700 text-white',
  },
  'diners-club': {
    label: 'Diners Club',
    Icon: SiDinersclub,
    iconColor: 'text-[#0079BE]',
    className: 'bg-gray-700 text-white',
  },
  elo: {
    label: 'Elo',
    Icon: CreditCard,
    iconColor: 'text-[#00A4E0]',
    className: 'bg-yellow-500 text-gray-900',
  },
  hipercard: {
    label: 'Hipercard',
    Icon: CreditCard,
    iconColor: 'text-[#822124]',
    className: 'bg-red-700 text-white',
  },
  hiper: {
    label: 'Hiper',
    Icon: CreditCard,
    iconColor: 'text-[#E8001D]',
    className: 'bg-red-600 text-white',
  },
};

function getBrandStyle(brandType: string | null): BrandStyle | null {
  if (!brandType) return null;
  return (
    BRAND_STYLES[brandType] ?? {
      label: brandType,
      Icon: CreditCard,
      iconColor: 'text-gray-500',
      className: 'bg-gray-600 text-white',
    }
  );
}

export function CardBrandBadge({
  brandType,
  insideInput = false,
}: {
  brandType: string | null;
  /** Ícone dentro do campo, alinhado à direita */
  insideInput?: boolean;
}) {
  const style = getBrandStyle(brandType);
  const Icon = style?.Icon ?? CreditCard;

  if (insideInput) {
    if (!brandType || !style) return null;
    return (
      <span
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center"
        title={style.label}
        aria-hidden
      >
        <Icon className={`w-8 h-8 ${style.iconColor}`} />
      </span>
    );
  }

  if (!brandType || !style) return null;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${style.className}`}
    >
      <Icon className={`w-5 h-5 ${style.iconColor}`} />
      {style.label}
    </span>
  );
}

export function isCreditCardMethod(tipoMetodo: string): boolean {
  return tipoMetodo === 'cartao_credito' || tipoMetodo === 'credit_card';
}
